import React from 'react';
import { Link } from 'react-router-dom';
import { PenLine, Share2, Heart, Wallet, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

const HowItWorks = () => {
  const { t } = useLanguage();
  const steps = [
    {
      icon: PenLine,
      title: 'Create Your Campaign',
      description: 'Set up your campaign in minutes. Add your story, photos, and fundraising goal.',
      features: ['Easy-to-use campaign builder', 'AI-powered content suggestions', 'Multiple categories supported'],
      color: 'emerald',
    },
    {
      icon: Share2,
      title: 'Share Your Story',
      description: 'Spread the word through social media, email, and your personal network.',
      features: ['One-click social sharing', 'Shareable campaign links', 'Email invite templates'],
      color: 'blue',
    },
    {
      icon: Heart,
      title: 'Receive Donations',
      description: 'Accept donations from anywhere in the world with secure payment processing.',
      features: ['Global payment support', 'Multiple currencies', 'Low platform fees'],
      color: 'rose',
    },
    {
      icon: Wallet,
      title: 'Withdraw Funds',
      description: 'Transfer funds directly to your bank account with our fast payout system.',
      features: ['Fast bank transfers', 'Milestone-based releases', 'Transparent tracking'],
      color: 'violet',
    },
  ];

  const colorClasses = {
    emerald: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
    blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    rose: { bg: 'bg-rose-500/20', text: 'text-rose-400', border: 'border-rose-500/30' },
    violet: { bg: 'bg-teal-500/20', text: 'text-teal-400', border: 'border-teal-500/30' },
  };

  return (
    <section className="py-12 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {t('howItWorksTitle')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('howItWorksSubtitle')}
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const colors = colorClasses[step.color];
            return (
              <div key={index} className="relative group">
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-border -translate-y-1/2 z-0">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-muted-foreground" />
                  </div>
                )}

                <div className="relative z-10 bg-card p-6 rounded-2xl border border-border shadow-sm hover:shadow-lg transition-all duration-300 h-full">
                  {/* Step Number */}
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>

                  {/* Icon */}
                  <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-8 h-8 ${colors.text}`} />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {step.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2">
                    {step.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-sm text-muted-foreground">
                        <CheckCircle2 className={`w-4 h-4 ${colors.text} mr-2 flex-shrink-0`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
